/*
 * This file is part of the Det Norske Teatret application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React from 'react';
import PropTypes from 'prop-types';
import Picture from 'components/Picture/Picture';
import Text from '../Text/Text';
import './ImageText.scss';

/**
 * This is the ImageText component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 *
 * @return {JSX}
 */
function ImageText({ layout, text, image, dropcap }) {
  function renderImage() {
    if (!image) {
      return <div className="empty" />;
    }

    const { description, ...rest } = image;

    function renderDescription() {
      if (!description) return null;

      return <figcaption dangerouslySetInnerHTML={{ __html: description }} />;
    }

    return (
      <figure className="image" appear="true">
        <Picture {...rest} />
        {renderDescription()}
      </figure>
    );
  }

  if (layout === 'left') {
    return (
      <div className="block image-text layout-left">
        {renderImage()}
        <Text dropcap={dropcap}>{text}</Text>
      </div>
    );
  }

  return (
    <div className="block image-text layout-right">
      <Text dropcap={dropcap}>{text}</Text>
      {renderImage()}
    </div>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
ImageText.propTypes = {
  layout: PropTypes.oneOf(['left', 'right']).isRequired,
  dropcap: PropTypes.bool,
  text: PropTypes.string,
  image: PropTypes.object,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
ImageText.defaultProps = {
  layout: 'left',
  text: '',
  dropcap: false,
  image: null,
};

export default ImageText;
